import React, { useEffect, useState, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Cookie } from 'lucide-react';
import { enableAnalytics, disableAnalytics } from '../utils/analytics';

const STORAGE_KEY = 'myguardpod-consent-v1';

/**
 * Cookie Consent Banner — Loi 25 compliant, Decision 8 hardened.
 *
 * Per Decisions 7 & 8 (rapport v2 §9):
 *   - GA4 and PostHog scripts are NOT loaded at boot (see index.html).
 *   - `enableAnalytics()` is called ONLY when the user clicks "Accept".
 *     It injects GA4 and PostHog script tags dynamically.
 *   - `disableAnalytics()` is called when the user withdraws consent via
 *     the footer "Privacy preferences" button. It removes the script tags,
 *     clears GA and PostHog cookies, and resets the PostHog identifier.
 *   - "Reject non-essential" leaves analytics fully unloaded (nothing was
 *     ever loaded in that path).
 */
export const CookieConsentBanner = () => {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [consent, setConsent] = useState(undefined); // undefined = still loading

  // Read stored decision on mount and apply side effects.
  useEffect(() => {
    let stored = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch (e) { /* localStorage may be unavailable */ }
    setConsent(stored);
    if (stored === 'all') {
      // Restore analytics on next visit if the user had previously accepted.
      enableAnalytics();
    }
    // 'essential' or null → do nothing; analytics stay unloaded.
  }, []);

  const decide = useCallback((choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, choice);
    } catch (e) { /* ignore */ }
    setConsent(choice);
    if (choice === 'all') {
      enableAnalytics();
    } else {
      // 'essential' → aggressively remove any pre-loaded trackers
      // (belt-and-braces, since nothing should have been loaded anyway).
      disableAnalytics();
    }
  }, []);

  // Expose a global helper so the footer link can re-open the banner
  // AND withdraw consent when re-visited.
  useEffect(() => {
    window.__openPrivacyPreferences = () => {
      // Show the banner again and unload analytics until the user re-decides.
      // This satisfies Decision 8 "arrêt de la collecte lors du retrait du
      // consentement + réinitialisation identifiant".
      disableAnalytics();
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) { /* ignore */ }
      setConsent(null);
    };
    return () => {
      delete window.__openPrivacyPreferences;
    };
  }, []);

  if (consent === undefined) return null;
  if (consent === 'all' || consent === 'essential') return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-body"
      className="fixed bottom-0 inset-x-0 z-[100] bg-[#1a1a1a] text-white border-t border-neutral-800 shadow-2xl"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-6 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <Cookie className="w-5 h-5 text-[#E31B23] flex-shrink-0 mt-1" aria-hidden="true" />
          <div>
            <h2 id="cookie-consent-title" className="font-outfit font-bold text-base sm:text-lg mb-1">
              {fr ? 'Vos pr\u00e9f\u00e9rences de confidentialit\u00e9' : 'Your privacy preferences'}
            </h2>
            <p id="cookie-consent-body" className="font-manrope text-xs sm:text-sm text-neutral-300 leading-relaxed">
              {fr
                ? 'Nous utilisons uniquement des t\u00e9moins essentiels pour faire fonctionner le site. Avec votre consentement, nous chargeons aussi des outils d\u2019analyse (Google Analytics, PostHog) pour mesurer la fr\u00e9quentation de fa\u00e7on agr\u00e9g\u00e9e. Aucun outil d\u2019analyse n\u2019est charg\u00e9 tant que vous n\u2019avez pas accept\u00e9. Aucun contenu de formulaire n\u2019est captur\u00e9.'
                : 'We use only essential cookies to run the site. With your consent, we also load analytics tools (Google Analytics, PostHog) to measure aggregate traffic. No analytics tool is loaded until you accept. No form content is captured.'}
            </p>
          </div>
        </div>
        <div
          className="flex flex-col sm:flex-row gap-2 sm:gap-3 flex-shrink-0"
          role="group"
          aria-label={fr ? 'Choix de consentement' : 'Consent choices'}
        >
          <button
            type="button"
            onClick={() => decide('essential')}
            className="font-manrope font-bold text-sm px-4 py-2.5 rounded-full border border-white text-white hover:bg-white hover:text-[#1a1a1a] transition-colors"
            data-testid="cookie-reject-nonessential"
          >
            {fr ? 'Refuser les t\u00e9moins non essentiels' : 'Reject non-essential cookies'}
          </button>
          <a
            href={fr ? '/fr/confidentialite' : '/en/privacy'}
            className="font-manrope font-semibold text-sm px-4 py-2.5 rounded-full text-neutral-300 hover:text-white underline decoration-dotted underline-offset-4 text-center"
            data-testid="cookie-customize"
          >
            {fr ? 'En savoir plus' : 'Learn more'}
          </a>
          <button
            type="button"
            onClick={() => decide('all')}
            className="font-manrope font-bold text-sm px-4 py-2.5 rounded-full bg-[#E31B23] hover:bg-[#c8171e] text-white transition-colors"
            data-testid="cookie-accept-all"
          >
            {fr ? 'Accepter' : 'Accept'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
